import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import translationKeys from 'translations/translationKeys';
import { printQzTicket, printQzHtmlTicket, QzEventType } from '@dme/ticket-print';
import { initializeQzKeys, isQzInitialized, parseLogoDimensions } from 'utils/utils';
import configs from '../../config';
import { checkPrinterStatus } from '../print-config/printConfigSlice'; // adjust path as needed

const apiBaseUrl = configs.BASE_URL_API_PRINT;
let enabledQz = false;

const client = axios.create({
  baseURL: apiBaseUrl,
});

const {
  msg23, msg29, msg46, msg116, msg147, msg159, msg220, msg229, msg275,
} = translationKeys;

/**
 * Maps a structured PrinterError from @dme/ticket-print to a translated message key.
 *
 * Priority:
 *  1. Specific statusText matches (most informative to the user)
 *  2. Severity-level fallback
 *  3. Ultimate fallback → msg159 (generic printer error)
 *
 * Printer-level statusText  (eventType === 'PRINTER')
 *   FATAL : ERROR, PAPER_JAM, OFFLINE, NOT_AVAILABLE, NO_TONER, PAGE_PUNT, OUT_OF_MEMORY, UNMAPPED
 *   WARN  : PAPER_OUT, PAPER_PROBLEM, PAUSED, OUTPUT_BIN_FULL, TONER_LOW,
 *            USER_INTERVENTION, DOOR_OPEN, PENDING_DELETION, SERVER_UNKNOWN
 *
 * Job-level statusText       (eventType === 'JOB')
 *   ERROR : ABORTED, ERROR, OFFLINE
 *   WARN  : CANCELED, PAPEROUT, RESTART, USER_INTERVENTION
 */
function getPageWidthFromCpl(cpl) {
  switch (cpl) {
    case 32: return 58;
    case 40: return 72;
    case 48: return 80;
    default: return 80;
  }
}

function getQzErrorMessage(printerError) {
  if (!printerError || !printerError?.statusText) return msg159;

  const { statusText, severity, eventType } = printerError;

  // ── Printer-level statuses ────────────────────────────────────────────────
  if (eventType === 'PRINTER') {
    switch (statusText) {
      // Printer is unreachable / switched off
      case 'OFFLINE':
      case 'NOT_AVAILABLE':
        return msg220; // 'L'imprimante n'est pas connectée'

      // Paper problems
      case 'PAPER_JAM':
      case 'PAPER_OUT':
      case 'PAPER_PROBLEM':
        return msg159; // 'Une erreur est survenue avec l'imprimante'

      // Cover open — operator must close it
      case 'DOOR_OPEN':
        return msg159; // 'Une erreur est survenue avec l'imprimante'

      // Hardware / toner / memory errors
      case 'ERROR':
      case 'NO_TONER':
      case 'OUT_OF_MEMORY':
      case 'PAGE_PUNT':
        return msg159; // 'Une erreur est survenue avec l'imprimante'

      // Printer paused or needs operator action
      case 'PAUSED':
      case 'USER_INTERVENTION':
      case 'OUTPUT_BIN_FULL':
      case 'TONER_LOW':
      case 'PENDING_DELETION':
      case 'SERVER_UNKNOWN':
        return msg159; // 'Une erreur est survenue avec l'imprimante'

      // Unknown / unmapped status
      case 'UNMAPPED':
      default:
        break;
    }
  }

  // ── Job-level statuses ────────────────────────────────────────────────────
  if (eventType === 'JOB') {
    switch (statusText) {
      // Job failed because printer is offline
      case 'OFFLINE':
        return msg220; // 'L'imprimante n'est pas connectée'

      // Job was explicitly cancelled
      case 'CANCELED':
        return msg229; // 'Une erreur s'est produite pendant le processus'

      // Job aborted or generic job error
      case 'ABORTED':
      case 'ERROR':
        return msg159; // 'Une erreur est survenue avec l'imprimante'

      // Paper ran out mid-job
      case 'PAPEROUT':
        return msg159; // 'Une erreur est survenue avec l'imprimante'

      // Operator must intervene / job restarting
      case 'USER_INTERVENTION':
      case 'RESTART':
        return msg159; // 'Une erreur est survenue avec l'imprimante'

      default:
        break;
    }
  }

  // ── Severity-level fallback ───────────────────────────────────────────────
  switch (severity) {
    case 'FATAL':
    case 'ERROR':
      return msg159; // 'Une erreur est survenue avec l'imprimante'
    case 'WARN':
      return msg159; // 'Une erreur est survenue avec l'imprimante'
    default:
      return msg229; // 'Une erreur s'est produite pendant le processus'
  }
}

export const printTicket = createAsyncThunk(
  'Tickets/print ticket',
  async (ticket, thunkAPI) => {
    const api = '/print-esc-pos';
    try {
      // Remove the two last lines that contain the flexbet signature
      let lines = [...ticket];
      lines.splice(lines.length - 2, 2);
      lines = lines.map((line) => line.split('\u0000').join(''));
      const bonusIndex = lines.findIndex((line) => line.includes('Bonus'));
      if (bonusIndex > -1) {
        lines.splice(bonusIndex, 1);
        lines.splice(5, 1, `${lines[5]} - FREE BET`);
      }

      const state = thunkAPI.getState();

      // 👇 THIS is the correct way
      const printerConfig = state.printerConfig.config;
      const { isDemoMode, waterMarkText } = state.printerConfig;
      const useQzPrint = isDemoMode || printerConfig.qzPrintingEnabled;

      if (useQzPrint) {
        enabledQz = true;
        // Initialize QZ keys if not already done
        if (!isQzInitialized()) {
          await initializeQzKeys();
          if (!isQzInitialized()) {
            return thunkAPI.rejectWithValue({
              success: false,
              message: msg116, // 'Autorisation échouée'
              method: 'qz-tray',
            });
          }
        }

        // ── NEW: check printer status before attempting to print ──────────────
        const statusResult = await thunkAPI.dispatch(
          checkPrinterStatus(printerConfig.printerName),
        );

        if (checkPrinterStatus.rejected.match(statusResult)) {
          // Status check itself failed (QZ comms error, timeout, etc.)
          return thunkAPI.rejectWithValue({
            success: false,
            message: msg275, // reuse the same key from printerConfigSlice
            method: 'qz-tray',
            printerError: statusResult.payload,
          });
        }

        const { isReady, statusText, severity } = statusResult.payload;

        if (!isReady) {
          // Printer is reachable but not ready — map to a translated message
          const error = { statusText, severity, eventType: QzEventType.PRINTER };
          return thunkAPI.rejectWithValue({
            success: false,
            message: getQzErrorMessage(error),
            method: 'qz-tray',
            printerError: error,
          });
        }

        let logoPath = configs.PRINT_LOGO || null;
        if (logoPath && !logoPath.includes('http')) {
          logoPath = `${process.env.PUBLIC_URL}/${logoPath}`;
        }

        const { maxLogoWidth, maxLogoHeight } = parseLogoDimensions(
          configs.PRINT_LOGO_SIZE,
        );
        // Print using QZ Tray
        // printQzTicket() and printQzHtmlTicket() always resolve — check result.success.
        const commonQzOptions = {
          lines,
          printerName: printerConfig.printerName,
          copies: printerConfig.count,
          cpl: printerConfig.cpl,
          logoPath,
          maxLogoWidth,
          maxLogoHeight,
        };

        let result;
        if (isDemoMode && waterMarkText) {
          result = await printQzHtmlTicket({
            ...commonQzOptions,
            maxLogoWidth: maxLogoWidth * 0.65,
            maxLogoHeight: maxLogoHeight * 0.65,
            pageSize: { width: getPageWidthFromCpl(printerConfig.cpl), unit: 'mm' },
            watermark: {
              type: 'text',
              content: waterMarkText,
              opacity: 0.3,
            },
          });
        } else {
          result = await printQzTicket(commonQzOptions);
        }

        if (result.success) {
          return {
            erc: 1,
            message: msg46, // 'succès!'
            method: 'qz-tray',
          };
        }

        // Map the structured PrinterError to a translated message
        const errorMessage = getQzErrorMessage(result.printerError);

        return thunkAPI.rejectWithValue({
          success: false,
          message: errorMessage,
          method: 'qz-tray',
          // Forward the raw error detail for logging / debugging
          printerError: result.printerError,
        });
      }

      enabledQz = false;

      // ── HTTP fallback printing ──────────────────────────────────────────────
      const resp = await client.post(api, { lines }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return {
        ...resp.data,
        method: 'http-client',
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Print error:', error);

      let errorMessage = msg159; // Default: 'Une erreur est survenue avec l'imprimante'
      const qzEnabled = enabledQz ?? false;

      if (!qzEnabled) {
        // HTTP client specific errors
        if (error.response) {
          if (error.response.status === 404) {
            errorMessage = msg220; // 'L'imprimante n'est pas connectée'
          } else if (error.response.status === 500) {
            errorMessage = msg29; // 'Une erreur est survenue sur le serveur'
          } else if (error.response.data?.message) {
            errorMessage = error.response.data.message;
          }
        } else if (error.request) {
          errorMessage = msg23; // 'Erreur de connexion, veuillez contacter le support !'
        }
      }

      // For QZ, thrown errors (e.g. network to QZ Tray, key loading) are
      // distinct from structured PrinterErrors — map them by message content.
      if (qzEnabled && error.message) {
        if (
          error.message.includes('Unable to establish connection')
          || error.message.includes('Printer not found')
        ) {
          errorMessage = msg220; // 'L'imprimante n'est pas connectée'
        } else if (error.message.includes('lost connection')) {
          errorMessage = msg23; // 'Erreur de connexion, veuillez contacter le support !'
        } else if (error.message.includes('Certificate')) {
          errorMessage = msg116; // 'Autorisation échouée'
        } else if (
          error.message.includes('lines must be an array')
          || error.message.includes('printerName is required')
        ) {
          errorMessage = msg147; // 'Paramètre manquant!'
        }
      }

      return thunkAPI.rejectWithValue({
        success: false,
        message: errorMessage,
        method: qzEnabled ? 'qz-tray' : 'http-client',
      });
    }
  },
);

const initialState = {
  printSuccess: false,
  isPrintLoading: false,
  printError: false,
  unPrintedTicket: false,
  checkedUnPrintedTicket: false,
  printErrorMessage: '',
  printMethod: 'gz-tray', // 'qz-tray' or 'http-client'
};

const printTicketSlice = createSlice({
  name: 'print ticket',
  initialState,
  reducers: {
    refreshPrinting: (state) => ({ ...state, ...initialState }),
    printing: (state) => ({
      ...state, isPrintLoading: true, printError: false, printErrorMessage: '',
    }),
    checkUnprintedTicket: (state) => {
      const unPrintedTicket = localStorage.getItem('currentTicket') !== null;

      return {
        ...state, unPrintedTicket, checkedUnPrintedTicket: true,
      };
    },
    setPrintStatus: (state, { payload }) => {
      if (payload.status) {
        localStorage.removeItem('currentTicket');
      }

      return {
        ...state,
        printError: !payload.status,
        printSuccess: payload.status,
        printErrorMessage: payload.status ? (payload.msg || msg159) : '',
        checkedUnPrintedTicket: false,
        isPrintLoading: false,
      };
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(printTicket.pending, (state) => ({
        ...state,
        isPrintLoading: true,
        printError: false,
      }))
      .addCase(printTicket.fulfilled, (state, { payload }) => {
        let printSuccess = false;
        let printError = true;
        let printErrorMessage = '';

        // Handle response based on method
        const isSuccess = Number(payload?.erc) === 1 || payload?.success === true;
        if (payload?.method === 'qz-tray') {
          if (isSuccess) {
            localStorage.removeItem('currentTicket');
            printSuccess = true;
            printError = false;
          } else {
            printErrorMessage = payload?.message || msg159;
          }
        } else if (payload?.method === 'http-client') {
          if (isSuccess) {
            localStorage.removeItem('currentTicket');
            printSuccess = true;
            printError = false;
          } else if (Number(payload?.erc) === 404) {
            printErrorMessage = msg220; // 'L'imprimante n'est pas connectée'
          } else {
            printErrorMessage = payload?.message || msg159;
          }
        } else {
          printErrorMessage = msg229; // 'Une erreur s'est produite pendant le processus'
        }

        return {
          ...state,
          printSuccess,
          isPrintLoading: false,
          printError,
          checkedUnPrintedTicket: false,
          printErrorMessage,
          printMethod: payload?.method,
        };
      })

      .addCase(printTicket.rejected, (state, { payload }) => ({
        ...state,
        isPrintLoading: false,
        printSuccess: false,
        printError: true,
        checkedUnPrintedTicket: false,
        printErrorMessage: payload?.message || msg159,
        printMethod: payload?.method,
      }));
  },
});

export const {
  refreshPrinting, checkUnprintedTicket,
  setPrintStatus, printing,
} = printTicketSlice.actions;

export default printTicketSlice.reducer;
